import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, Clock, ArrowRight, Shield, AlertTriangle, 
  FileText, TrendingUp, Lock, Eye, Check, Loader2
} from 'lucide-react';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [visiblePosts, setVisiblePosts] = useState(4);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle, loading, success, error

  const featuredPost = {
    id: 1,
    title: 'PPSI 2.0: O Novo Padrão de Segurança para o Setor Público',
    excerpt: 'Entenda as mudanças do PPSI 2.0 e como sua organização pode se adequar aos novos requisitos de segurança da informação.',
    category: 'Compliance',
    date: '15 Fev 2026',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=400&fit=crop',
    slug: 'ppsi-2-novo-padrao-seguranca'
  };

  const allPosts = [
    {
      id: 2,
      title: 'Ransomware em 2026: Tendências e Como se Proteger',
      excerpt: 'Análise das principais ameaças de ransomware e estratégias eficazes de prevenção.',
      category: 'Cybersegurança',
      date: '12 Fev 2026',
      readTime: '6 min',
      icon: AlertTriangle,
      color: 'text-red-500 bg-red-500/10'
    },
    {
      id: 3,
      title: 'ISO 27001:2022 - Principais Mudanças e Impactos',
      excerpt: 'Guia completo sobre as atualizações da norma e como implementá-las.',
      category: 'Compliance',
      date: '08 Fev 2026',
      readTime: '10 min',
      icon: FileText,
      color: 'text-blue-500 bg-blue-500/10'
    },
    {
      id: 4,
      title: 'Gestão de Riscos: Metodologias e Ferramentas',
      excerpt: 'Compare as principais metodologias de gestão de riscos e descubra qual é ideal para sua organização.',
      category: 'GRC',
      date: '05 Fev 2026',
      readTime: '7 min',
      icon: TrendingUp,
      color: 'text-emerald-500 bg-emerald-500/10'
    },
    {
      id: 5,
      title: 'LGPD: 5 Anos Depois - Lições Aprendidas',
      excerpt: 'Reflexões sobre a implementação da LGPD no Brasil e próximos passos.',
      category: 'Privacidade',
      date: '01 Fev 2026',
      readTime: '5 min',
      icon: Lock,
      color: 'text-purple-500 bg-purple-500/10'
    },
    {
      id: 6,
      title: 'SOC: Construindo um Centro de Operações de Segurança',
      excerpt: 'Passo a passo para implementar um SOC eficiente em sua organização.',
      category: 'Cybersegurança',
      date: '28 Jan 2026',
      readTime: '12 min',
      icon: Eye,
      color: 'text-cyan-500 bg-cyan-500/10'
    },
    {
      id: 7,
      title: 'Zero Trust: Arquitetura de Segurança Moderna',
      excerpt: 'Entenda o modelo Zero Trust e como aplicá-lo em sua infraestrutura.',
      category: 'Arquitetura',
      date: '25 Jan 2026',
      readTime: '9 min',
      icon: Shield,
      color: 'text-orange-500 bg-orange-500/10'
    },
    {
      id: 8,
      title: 'Auditoria de Segurança: Guia Prático',
      excerpt: 'Como conduzir auditorias de segurança eficazes em sua organização.',
      category: 'GRC',
      date: '20 Jan 2026',
      readTime: '11 min',
      icon: FileText,
      color: 'text-blue-500 bg-blue-500/10'
    },
    {
      id: 9,
      title: 'Proteção de Dados Pessoais na Prática',
      excerpt: 'Implementando controles de privacidade conforme a LGPD.',
      category: 'Privacidade',
      date: '15 Jan 2026',
      readTime: '8 min',
      icon: Lock,
      color: 'text-purple-500 bg-purple-500/10'
    }
  ];

  // Calculate category counts dynamically
  const getCategoryCount = (categoryName) => {
    if (categoryName === 'Todos') {
      return allPosts.length + 1; // +1 for featured post
    }
    let count = allPosts.filter(p => p.category === categoryName).length;
    if (featuredPost.category === categoryName) count++;
    return count;
  };

  const categories = [
    { name: 'Todos' },
    { name: 'Cybersegurança' },
    { name: 'Compliance' },
    { name: 'GRC' },
    { name: 'Privacidade' },
    { name: 'Arquitetura' }
  ];

  // Filter posts based on active category
  const filteredPosts = activeCategory === 'Todos' 
    ? allPosts 
    : allPosts.filter(post => post.category === activeCategory);

  // Show featured post only if category matches or "Todos"
  const showFeatured = activeCategory === 'Todos' || featuredPost.category === activeCategory;

  // Posts to display (with pagination)
  const displayedPosts = filteredPosts.slice(0, visiblePosts);
  const hasMorePosts = filteredPosts.length > visiblePosts;

  const handleLoadMore = () => {
    setVisiblePosts(prev => prev + 4);
  };

  const handleCategoryClick = (categoryName) => {
    setActiveCategory(categoryName);
    setVisiblePosts(4); // Reset pagination when changing category
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterStatus('loading');

    try {
      const response = await fetch('https://formspree.io/f/mojnlvad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: newsletterEmail, 
          source: 'Blog Newsletter',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
        // Reset after 5 seconds
        setTimeout(() => setNewsletterStatus('idle'), 5000);
      } else {
        setNewsletterStatus('error');
        setTimeout(() => setNewsletterStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Error:', error);
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Blog & Insights - DMM Security</title>
        <meta name="description" content="Artigos, alertas e dicas sobre cybersegurança, compliance, GRC e segurança da informação." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24" data-testid="blog-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-white/10 rounded-full text-sm font-medium mb-4">
                Blog & Insights
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Conhecimento em Segurança
              </h1>
              <p className="text-lg text-gray-200">
                Artigos, alertas de vulnerabilidades e dicas práticas para elevar a maturidade de segurança da sua organização
              </p>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <h3 className="font-bold text-[#1a3a52] mb-4">Categorias</h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.name)}
                      data-testid={`category-${cat.name.toLowerCase()}`}
                      className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-all ${
                        activeCategory === cat.name
                          ? 'bg-[#10b981] text-white' 
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className={`text-xs ${activeCategory === cat.name ? 'bg-white/20' : 'bg-gray-100'} px-2 py-0.5 rounded-full`}>
                        {getCategoryCount(cat.name)}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Newsletter Signup */}
                <div className="mt-8 p-6 bg-gradient-to-br from-[#1a3a52] to-[#2a5a72] rounded-xl text-white">
                  <h4 className="font-bold mb-2">Newsletter</h4>
                  <p className="text-sm text-gray-300 mb-4">
                    Receba novos artigos direto no seu email
                  </p>
                  
                  {newsletterStatus === 'success' ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2 py-3 px-4 bg-[#10b981]/20 rounded-lg text-[#10b981]"
                    >
                      <Check className="w-5 h-5" />
                      <span className="text-sm font-medium">Inscrito com sucesso!</span>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleNewsletterSubmit}>
                      <input
                        type="email"
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        placeholder="seu@email.com"
                        required
                        disabled={newsletterStatus === 'loading'}
                        className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm mb-3 disabled:opacity-50"
                        data-testid="newsletter-email"
                      />
                      <button 
                        type="submit"
                        disabled={newsletterStatus === 'loading'}
                        className="w-full bg-[#10b981] hover:bg-[#0ea573] text-white py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="newsletter-submit"
                      >
                        {newsletterStatus === 'loading' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Inscrevendo...
                          </>
                        ) : (
                          'Inscrever-se'
                        )}
                      </button>
                      {newsletterStatus === 'error' && (
                        <p className="text-red-400 text-xs mt-2">Erro ao inscrever. Tente novamente.</p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Featured Post */}
              <AnimatePresence mode="wait">
                {showFeatured && (
                  <motion.article
                    key="featured"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all mb-8 group"
                  >
                    <div className="md:flex">
                      <div className="md:w-1/2">
                        <img
                          src={featuredPost.image}
                          alt={featuredPost.title}
                          className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="md:w-1/2 p-8 flex flex-col justify-center">
                        <span className="inline-block px-3 py-1 bg-[#10b981]/10 text-[#10b981] text-xs font-semibold rounded-full mb-4 w-fit">
                          {featuredPost.category}
                        </span>
                        <h2 className="text-2xl font-bold text-[#1a3a52] mb-3 group-hover:text-[#10b981] transition-colors">
                          {featuredPost.title}
                        </h2>
                        <p className="text-gray-600 mb-4">
                          {featuredPost.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {featuredPost.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {featuredPost.readTime}
                          </span>
                        </div>
                        <Link
                          to={`/blog/${featuredPost.slug}`}
                          className="inline-flex items-center gap-2 text-[#10b981] font-semibold hover:gap-3 transition-all"
                        >
                          Ler artigo completo
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                )}
              </AnimatePresence>

              {/* Category indicator */}
              {activeCategory !== 'Todos' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-6 flex items-center gap-2"
                >
                  <span className="text-gray-500">Filtrando por:</span>
                  <span className="px-3 py-1 bg-[#10b981] text-white text-sm font-medium rounded-full">
                    {activeCategory}
                  </span>
                  <button
                    onClick={() => handleCategoryClick('Todos')}
                    className="text-sm text-gray-500 hover:text-[#10b981] underline"
                  >
                    Limpar filtro
                  </button>
                </motion.div>
              )}

              {/* Posts Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <AnimatePresence mode="popLayout">
                  {displayedPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all group cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${post.color} flex items-center justify-center flex-shrink-0`}>
                          <post.icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {post.category}
                          </span>
                          <h3 className="font-bold text-[#1a3a52] mt-1 mb-2 group-hover:text-[#10b981] transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {post.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {post.readTime}
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#10b981] opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>

              {/* No results message */}
              {filteredPosts.length === 0 && !showFeatured && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <p className="text-gray-500">Nenhum artigo encontrado nesta categoria.</p>
                  <button
                    onClick={() => handleCategoryClick('Todos')}
                    className="mt-4 text-[#10b981] font-medium hover:underline"
                  >
                    Ver todos os artigos
                  </button>
                </motion.div>
              )}

              {/* Load More */}
              {hasMorePosts && (
                <motion.div 
                  className="text-center mt-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <button 
                    onClick={handleLoadMore}
                    className="px-8 py-3 bg-[#1a3a52] text-white rounded-xl font-semibold hover:bg-[#0f2535] transition-colors"
                    data-testid="load-more-btn"
                  >
                    Carregar mais artigos ({filteredPosts.length - visiblePosts} restantes)
                  </button>
                </motion.div>
              )}

              {/* All loaded message */}
              {!hasMorePosts && filteredPosts.length > 0 && (
                <p className="text-center text-gray-500 mt-10 text-sm">
                  Todos os artigos foram carregados
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
